import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { loadDashboard } from '../services/dashboardService';
import { formatMoney } from '../lib/format';
import { friendlyError } from '../lib/errors';
import StatCard from '../components/ui/StatCard';
import LoadingState from '../components/ui/LoadingState';
import ErrorState from '../components/ui/ErrorState';
import EmptyState from '../components/ui/EmptyState';
import Badge from '../components/ui/Badge';

export default function DashboardPage() {
  const { ruc, theme } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!ruc) return;
    setLoading(true);
    setError(null);
    try {
      const result = await loadDashboard({ ruc });
      setData(result);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }, [ruc]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) return <LoadingState label="Cargando indicadores…" />;
  if (error) return <ErrorState message={error} onRetry={fetchData} />;
  if (!data) return <EmptyState />;

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Dashboard</h1>
          <p className="muted">{theme?.tagline} · datos del día de hoy</p>
        </div>
        <Link className="btn btn-secondary" to="/admin/productos">Ver productos</Link>
      </header>

      <section className="stat-grid">
        <StatCard label="Ventas hoy" value={formatMoney(data.ventasHoy)} tone="primary" />
        <StatCard label="Comprobantes" value={data.comprobantesHoy} hint="boletas + facturas de hoy" />
        <StatCard
          label="Stock bajo"
          value={data.stockBajo}
          tone={data.stockBajo > 0 ? 'warn' : 'default'}
          hint="productos en o bajo mínimo"
        />
        <StatCard label="Clientes" value={data.totalClientes} hint="mínimo 1 documento" />
        <StatCard label="Caja" value={formatMoney(data.cajaHoy)} />
      </section>

      <section className="panel-grid">
        <article className="panel">
          <header className="panel-header">
            <h2>Últimos comprobantes</h2>
            <Link to="/admin/comprobantes">Ver todos</Link>
          </header>
          {data.ultimosComprobantes.length === 0 ? (
            <EmptyState title="Sin comprobantes recientes" />
          ) : (
            <ul className="simple-list">
              {data.ultimosComprobantes.map((c) => (
                <li key={c.id}>
                  <div>
                    <strong>{c.serie}-{c.correlativo}</strong>
                    <div className="muted">{c.rznSocial || 'Cliente'}</div>
                  </div>
                  <div className="row-end">
                    <span>{formatMoney(c.total)}</span>
                    <Badge tone={c.aceptado ? 'ok' : 'warn'}>
                      {c.aceptado ? 'Aceptado' : 'Pendiente'}
                    </Badge>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </article>

        <article className="panel">
          <header className="panel-header">
            <h2>Productos con stock bajo</h2>
            <Link to="/admin/inventario">Inventario</Link>
          </header>
          {data.productosStockBajo.length === 0 ? (
            <EmptyState title="Sin alertas de stock" />
          ) : (
            <ul className="simple-list">
              {data.productosStockBajo.map((p) => (
                <li key={p.uid}>
                  <div>
                    <strong>{p.name}</strong>
                    <div className="muted">Mín. {p.stockMinimo || 0}</div>
                  </div>
                  <Badge tone="warn">Stock {p.stock ?? 0}</Badge>
                </li>
              ))}
            </ul>
          )}
        </article>
      </section>

      <p className="reads-note muted">
        Lecturas Firestore de este dashboard: {Object.entries(data.readsEstimados)
          .map(([k, v]) => `${k}=${v}`)
          .join(' · ')}
      </p>
    </div>
  );
}
